import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { defaultCoreConfig } from '@cfleet/api-client';
import { CheckCircle2, AlertCircle, Loader2, Mail, ShieldCheck } from 'lucide-react';

/**
 * VerifyEmail Component
 *
 * Handles email verification for dispatcher users.
 * Extracts token from URL query params and calls the verification API.
 *
 * URL format: /verify-email?token=<verification_token>
 *
 * Flow:
 * 1. Extract token from URL
 * 2. Call POST /api/v1/auth/verify-email with { token }
 * 3. Show success/error state
 * 4. Redirect to login on success
 */

const API_BASE_URL = defaultCoreConfig.baseUrl;

interface VerifyEmailState {
  status: 'loading' | 'success' | 'error' | 'no-token';
  message: string;
}

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<VerifyEmailState>({
    status: 'loading',
    message: 'Verifying your email...',
  });
  const [resendEmail, setResendEmail] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendError, setResendError] = useState<string | null>(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setState({
        status: 'no-token',
        message: 'No verification token provided. Please check your email link.',
      });
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/verify-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.detail || errorData.message || 'Email verification failed. The link may be expired or invalid.'
          );
        }

        setState({
          status: 'success',
          message: 'Your email has been verified successfully!',
        });

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', {
            state: { emailVerified: true },
            replace: true,
          });
        }, 3000);
      } catch (err) {
        setState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Email verification failed',
        });
      }
    };

    verifyEmail();
  }, [token, navigate]);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleResendVerification = async () => {
    if (!resendEmail || resendCooldown > 0) return;
    setResendError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-email/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: resendEmail }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || 'Failed to resend verification email');
      }
      setResendSuccess(true);
      setResendCooldown(60);
    } catch (err) {
      setResendError(err instanceof Error ? err.message : 'Failed to resend verification email');
    }
  };

  const renderResendForm = () => (
    <div className="mt-6 pt-6 border-t border-slate-200">
      <p className="text-sm text-slate-500 mb-3">Need a new verification link?</p>
      <div className="flex gap-2">
        <input
          type="email"
          value={resendEmail}
          onChange={(e) => setResendEmail(e.target.value)}
          placeholder="Enter your email"
          className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 placeholder:text-slate-400"
        />
        <button
          onClick={handleResendVerification}
          disabled={!resendEmail || resendCooldown > 0}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
        </button>
      </div>
      {resendSuccess && (
        <p className="mt-2 text-sm text-emerald-600">Verification email sent! Check your inbox.</p>
      )}
      {resendError && (
        <p className="mt-2 text-sm text-rose-600">{resendError}</p>
      )}
    </div>
  );

  const renderContent = () => {
    switch (state.status) {
      case 'loading':
        return (
          <div className="text-center">
            <div className="w-16 h-16 bg-cfleet-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 size={32} className="text-cfleet-600 animate-spin" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Verifying Your Email</h2>
            <p className="text-slate-500">{state.message}</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-in zoom-in duration-300">
              <CheckCircle2 size={32} className="text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Email Verified!</h2>
            <p className="text-slate-500 mb-6">{state.message}</p>
            <p className="text-sm text-slate-400">Redirecting to login...</p>
            <Link
              to="/login"
              className="mt-4 inline-block text-cfleet-600 font-medium hover:underline"
            >
              Click here if not redirected
            </Link>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle size={32} className="text-rose-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Verification Failed</h2>
            <p className="text-slate-500 mb-6">{state.message}</p>
            <div className="space-y-3">
              <Link
                to="/login"
                className="block w-full py-3 px-4 bg-cfleet-600 text-white rounded-lg font-medium hover:bg-cfleet-700 transition-colors"
              >
                Go to Login
              </Link>
              <button
                onClick={() => window.location.reload()}
                className="block w-full py-3 px-4 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors"
              >
                Try Again
              </button>
            </div>
            {renderResendForm()}
          </div>
        );

      case 'no-token':
        return (
          <div className="text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail size={32} className="text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Missing Verification Token</h2>
            <p className="text-slate-500 mb-6">{state.message}</p>
            <Link
              to="/login"
              className="block w-full py-3 px-4 bg-cfleet-600 text-white rounded-lg font-medium hover:bg-cfleet-700 transition-colors"
            >
              Go to Login
            </Link>
            {renderResendForm()}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 font-sans text-slate-900 relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-cfleet-100/50 to-transparent -z-10"></div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in-95 duration-500">
        <div className="p-8">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 bg-cfleet-600 rounded-lg flex items-center justify-center text-white text-sm font-bold shadow-lg shadow-cfleet-500/30">
                C
              </div>
              <span className="font-bold text-3xl text-slate-800 tracking-tight">cfleet</span>
            </div>
          </div>

          {renderContent()}
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-8 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 text-xs text-emerald-600 font-medium bg-emerald-50 px-3 py-1 rounded-full w-fit mx-auto border border-emerald-100">
          <ShieldCheck size={12} /> Secure Connection
        </div>
        <p className="text-xs text-slate-400">© 2025 cfleet. All rights reserved.</p>
      </div>
    </div>
  );
};

export default VerifyEmail;
